import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Target, Play, Clock, MapPin, ChevronRight, Settings,
  LogOut, Users, Flag, Zap, CheckCircle2, X, ArrowLeft, Trophy, Plus
} from "lucide-react";
import { toast } from "sonner";
import { safeGet, safeSet, safeGetJSON, safeRemove } from "../utils/safeStorage";

const API = window.location.origin;

const ON_COURSE_FORMATS = [
  { value: "1_ball_score", label: "1-Ball Score", desc: "Play normally, count score" },
  { value: "2_ball_scramble", label: "2-Ball Scramble", desc: "Hit two, play the better result" },
  { value: "2_ball_worst_ball", label: "2-Ball Worst Ball", desc: "Hit two, play the worse result" },
  { value: "4_club_round", label: "4-Club Round", desc: "Pick 4 clubs only" },
  { value: "tee_shot_constraint", label: "Tee Shot Constraint", desc: "No driver off the tee" },
  { value: "approach_discipline", label: "Approach Discipline", desc: "One more club on every approach" },
  { value: "fairway_green_challenge", label: "Fairway + Green", desc: "Track fairways + GIR, ignore score" },
  { value: "up_down_challenge", label: "Up & Down Challenge", desc: "Track up-and-down conversion" },
];

const MANUAL_AREAS = [
  { id: "range", label: "Range", options: [30, 40, 50, 60, 80, 100], unit: "balls", location: "range", valueKey: "balls" },
  { id: "putting", label: "Putting", options: [15, 20, 25, 30, 45], unit: "min", location: "green", valueKey: "duration_minutes" },
  { id: "short_game_0_20", label: "Short Game (0-20)", options: [15, 20, 25, 30, 45], unit: "min", location: "short_game", valueKey: "duration_minutes", primary_skill: "short_game_0_20" },
  { id: "short_game_20_40", label: "Short Game (20-40)", options: [15, 20, 25, 30, 45], unit: "min", location: "short_game", valueKey: "duration_minutes", primary_skill: "short_game_20_40" },
  { id: "wedge_play", label: "Wedge Play (40-100)", options: [30, 40, 50, 60, 80, 100], unit: "balls", location: "range", valueKey: "balls", primary_skill: "wedge_play_40_100" },
  { id: "course", label: "On Course", options: [3, 6, 9, 18], unit: "holes", location: "course", valueKey: "holes" },
];

// Matches Session page BALL_TIME ranges (midpoint)
const BALL_TIME_MID = { 30: 25, 40: 35, 50: 45, 60: 55, 80: 70, 100: 90 };
function ballsToApproxTime(balls) {
  if (!balls) return "";
  const mid = BALL_TIME_MID[balls] || Math.round(balls * 0.75);
  return `~${mid} min`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missionLoading, setMissionLoading] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);
  const [resumeSession, setResumeSession] = useState(null);
  const [weeklyStatus, setWeeklyStatus] = useState(null);
  const [appVersion, setAppVersion] = useState(null);

  // Flow states
  const [activeFlow, setActiveFlow] = useState(null); // "range" | "putting" | "short_game" | "course" | "wedge_play"
  const [sgDistance, setSgDistance] = useState(null); // "0_20" | "20_40"
  const [courseMode, setCourseMode] = useState(null); // "practice" | "competitive"
  const [courseSetup, setCourseSetup] = useState({ holes: 9, format: "1_ball_score", course_name: "", focus: "" });

  // Manual session builder state
  const [manualBlocks, setManualBlocks] = useState([]);

  const addManualBlock = (areaId) => {
    const area = MANUAL_AREAS.find(a => a.id === areaId);
    if (!area) return;
    setManualBlocks(prev => [...prev, {
      areaId,
      label: area.label,
      value: area.options[0],
      unit: area.unit
    }]);
  };

  const removeManualBlock = (idx) => {
    setManualBlocks(prev => prev.filter((_, i) => i !== idx));
  };

  const updateManualBlock = (idx, value) => {
    setManualBlocks(prev => prev.map((b, i) => i === idx ? { ...b, value } : b));
  };

  const estimateBlockLabel = (block) => {
    const area = MANUAL_AREAS.find(a => a.id === block.areaId);
    if (!area) return "";
    if (area.unit === "balls") {
      const mid = BALL_TIME_MID[block.value] || Math.round(block.value * 0.75);
      return `~${mid} min`;
    }
    if (area.unit === "holes") return `~${block.value * 12} min`;
    return `${block.value} min`;
  };

  const totalManualLabel = (() => {
    let totalMin = 0;
    for (const block of manualBlocks) {
      const area = MANUAL_AREAS.find(a => a.id === block.areaId);
      if (!area) continue;
      if (area.unit === "balls") totalMin += (BALL_TIME_MID[block.value] || Math.round(block.value * 0.75));
      else if (area.unit === "holes") totalMin += block.value * 12;
      else totalMin += block.value;
    }
    if (totalMin === 0) return "";
    const hrs = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    if (hrs > 0 && mins > 0) return `~${hrs}h ${mins}m`;
    if (hrs > 0) return `~${hrs}h`;
    return `~${totalMin} min`;
  })();

  const buildManualSessionBlocks = () => {
    return manualBlocks.filter(b => b.value).map(block => {
      const area = MANUAL_AREAS.find(a => a.id === block.areaId);
      if (!area) return null;
      const sb = { location: area.location };
      if (area.valueKey === "balls") sb.balls = block.value;
      else if (area.valueKey === "duration_minutes") sb.duration_minutes = block.value;
      else if (area.valueKey === "holes") {
        sb.holes = block.value;
        sb.course_mode = "practice";
        sb.format = "1_ball_score";
        sb.primary_skill = "course";
      }
      if (area.primary_skill) sb.primary_skill = area.primary_skill;
      return sb;
    }).filter(Boolean);
  };

  useEffect(() => {
    fetchUserData();
    fetchRecentSessions();
    fetchWeeklyStatus();
    fetchVersion();
    validateResumeSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateResumeSession = async () => {
    try {
      const saved = safeGetJSON("gfp_active_session");
      if (!saved?.missionId) return;
      if (Date.now() - (saved.savedAt || 0) >= 24 * 60 * 60 * 1000) { safeRemove("gfp_active_session"); return; }
      const res = await authFetch(`${API}/api/sessions/${saved.missionId}`);
      if (!res.ok) { safeRemove("gfp_active_session"); return; }
      const data = await res.json();
      if (data.status === "completed" || data.status === "abandoned" || data.status === "pending") { safeRemove("gfp_active_session"); return; }
      setResumeSession(saved);
    } catch (e) { safeRemove("gfp_active_session"); }
  };

  const fetchUserData = async () => {
    try {
      const response = await authFetch(`${API}/api/users/me`);
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        if (!data.onboarding_complete) navigate("/onboarding");
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const fetchRecentSessions = async () => {
    try {
      const response = await authFetch(`${API}/api/sessions/recent`);
      if (response.ok) { setRecentSessions((await response.json()).sessions || []); }
    } catch (error) { console.error(error); }
  };

  const fetchWeeklyStatus = async () => {
    try {
      const res = await authFetch(`${API}/api/weekly-plan/status`);
      if (res.ok) setWeeklyStatus(await res.json());
    } catch (e) {}
  };

  const fetchVersion = async () => {
    try { const res = await fetch(`${API}/api/version`); if (res.ok) setAppVersion(await res.json()); } catch (e) {}
  };

  const quickStart = async (blocks) => {
    setMissionLoading(true);
    try {
      const res = await authFetch(`${API}/api/missions/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_blocks: blocks })
      });
      if (res.ok) {
        const mission = await res.json();
        const isOnCourse = mission.is_on_course || mission.environment === "course";
        navigate(isOnCourse ? `/on-course/${mission.mission_id}` : `/session/${mission.mission_id}`);
      } else if (res.status === 403) {
        toast.error("Upgrade to Pro for unlimited sessions");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate session");
      }
    } catch (error) { toast.error("Something went wrong"); }
    finally { setMissionLoading(false); }
  };

  const handleLogout = async () => {
    try { await authFetch(`${API}/api/auth/logout`, { method: "POST" }); clearToken(); navigate("/"); }
    catch (error) { console.error(error); }
  };

  const resetFlow = () => {
    setActiveFlow(null);
    setSgDistance(null);
    setCourseMode(null);
    setCourseSetup({ holes: 9, format: "1_ball_score", course_name: "", focus: "" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── SUB-FLOWS ──

  const renderRangeFlow = () => (
    <div className="animate-fade-in" data-testid="range-flow">
      <FlowHeader title="Range Session" onBack={resetFlow} />
      <p className="text-gray-700 text-sm mb-6 font-body">How many balls?</p>
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[30, 40, 50, 60, 80, 100].map(b => (
          <button key={b} onClick={() => quickStart([{ location: "range", balls: b }])}
            disabled={missionLoading}
            className="h-20 bg-white border-2 border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-1 hover:border-green-900 active:scale-95 transition-all"
            data-testid={`range-balls-${b}`}>
            <span className="text-2xl font-extrabold text-gray-900 font-heading">{b}</span>
            <span className="text-xs text-gray-600 font-body">{ballsToApproxTime(b)}</span>
          </button>
        ))}
      </div>
      {missionLoading && <LoadingIndicator />}
    </div>
  );

  const renderPuttingFlow = () => (
    <div className="animate-fade-in" data-testid="putting-flow">
      <FlowHeader title="Putting" onBack={resetFlow} />
      <p className="text-gray-700 text-sm mb-6 font-body">How long?</p>
      <div className="grid grid-cols-2 gap-3 mb-8">
        {[15, 20, 30, 45].map(d => (
          <button key={d} onClick={() => quickStart([{ location: "green", duration_minutes: d }])}
            disabled={missionLoading}
            className="h-20 bg-white border-2 border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-1 hover:border-green-900 active:scale-95 transition-all"
            data-testid={`putting-dur-${d}`}>
            <span className="text-2xl font-extrabold text-gray-900 font-heading">{d}</span>
            <span className="text-xs text-gray-600 font-body">minutes</span>
          </button>
        ))}
      </div>
      {missionLoading && <LoadingIndicator />}
    </div>
  );

  const renderShortGameFlow = () => {
    if (!sgDistance) {
      return (
        <div className="animate-fade-in" data-testid="short-game-flow">
          <FlowHeader title="Short Game" onBack={resetFlow} />
          <p className="text-gray-700 text-sm mb-6 font-body">What distance?</p>
          <div className="space-y-3">
            <button onClick={() => setSgDistance("0_20")}
              className="w-full h-20 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-between px-6 hover:border-green-900 active:scale-[0.98] transition-all"
              data-testid="sg-distance-0-20">
              <div>
                <p className="text-xl font-bold text-gray-900 font-heading">0 – 20 yards</p>
                <p className="text-xs text-gray-600 font-body">Chips, runners, bunker shots</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
            <button onClick={() => setSgDistance("20_40")}
              className="w-full h-20 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-between px-6 hover:border-green-900 active:scale-[0.98] transition-all"
              data-testid="sg-distance-20-40">
              <div>
                <p className="text-xl font-bold text-gray-900 font-heading">20 – 40 yards</p>
                <p className="text-xs text-gray-600 font-body">Pitches, trajectory, longer bunker</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="animate-fade-in" data-testid="short-game-time-flow">
        <FlowHeader title={sgDistance === "0_20" ? "Short Game (0-20)" : "Short Game (20-40)"} onBack={() => setSgDistance(null)} />
        <p className="text-gray-700 text-sm mb-6 font-body">How long?</p>
        <div className="grid grid-cols-2 gap-3 mb-8">
          {[15, 20, 30, 45].map(d => (
            <button key={d}
              onClick={() => quickStart([{
                location: "short_game",
                duration_minutes: d,
                primary_skill: sgDistance === "0_20" ? "short_game_0_20" : "short_game_20_40"
              }])}
              disabled={missionLoading}
              className="h-20 bg-white border-2 border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-1 hover:border-green-900 active:scale-95 transition-all"
              data-testid={`sg-dur-${d}`}>
              <span className="text-2xl font-extrabold text-gray-900 font-heading">{d}</span>
              <span className="text-xs text-gray-600 font-body">minutes</span>
            </button>
          ))}
        </div>
        {missionLoading && <LoadingIndicator />}
      </div>
    );
  };

  const renderWedgePlayFlow = () => (
    <div className="animate-fade-in" data-testid="wedge-play-flow">
      <FlowHeader title="Wedge Play (40-100 yds)" onBack={resetFlow} />
      <p className="text-gray-700 text-sm mb-6 font-body">How many balls?</p>
      <div className="grid grid-cols-2 gap-3 mb-8">
        {[30, 50, 70, 100].map(b => (
          <button key={b}
            onClick={() => quickStart([{ location: "range", balls: b, primary_skill: "wedge_play_40_100" }])}
            disabled={missionLoading}
            className="h-20 bg-white border-2 border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-1 hover:border-green-900 active:scale-95 transition-all"
            data-testid={`wedge-balls-${b}`}>
            <span className="text-2xl font-extrabold text-gray-900 font-heading">{b}</span>
            <span className="text-xs text-gray-600 font-body">{ballsToApproxTime(b)}</span>
          </button>
        ))}
      </div>
      {missionLoading && <LoadingIndicator />}
    </div>
  );


  const renderBuildFlow = () => (
    <div className="animate-fade-in" data-testid="build-session-flow">
      <FlowHeader title="Build Today's Session" onBack={() => { resetFlow(); setManualBlocks([]); }} />

      {/* Added blocks */}
      {manualBlocks.length > 0 && (
        <div className="space-y-2 mb-4">
          {manualBlocks.map((block, idx) => {
            const area = MANUAL_AREAS.find(a => a.id === block.areaId);
            if (!area) return null;
            return (
              <div key={idx} className="bg-white border-2 border-gray-200 rounded-xl p-3" data-testid={`build-block-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-gray-900 font-heading">{block.label}</span>
                  <button onClick={() => removeManualBlock(idx)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`build-remove-${idx}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {area.options.map(opt => {
                    const isActive = block.value === opt;
                    return (
                      <button key={opt} onClick={() => updateManualBlock(idx, opt)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${isActive ? "bg-green-900 border-green-900 text-white shadow-sm" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}
                        data-testid={`build-block-${idx}-opt-${opt}`}>
                        {opt} {block.unit}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1.5 font-body">{estimateBlockLabel(block)}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Add area buttons */}
      {manualBlocks.length < 5 && (
        <div className="mb-5">
          <p className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-2 font-body">
            {manualBlocks.length === 0 ? "What's available today?" : "Add another area"}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {MANUAL_AREAS.filter(a => {
              // Allow multiple range blocks (irons + wedges) but not duplicate other areas
              if (a.id === "range" || a.id === "wedge_play") return true;
              return !manualBlocks.some(b => b.areaId === a.id);
            }).map(area => (
              <button key={area.id} onClick={() => addManualBlock(area.id)}
                className="p-3 rounded-xl border-2 border-dashed border-gray-300 text-xs font-medium text-gray-600 hover:border-green-400 hover:text-green-800 hover:bg-green-50/50 transition-all flex items-center gap-2"
                data-testid={`build-add-${area.id}`}>
                <Plus className="w-3.5 h-3.5" /> {area.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Total estimate */}
      {manualBlocks.length > 0 && (
        <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-3 mb-5" data-testid="build-total-time">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 font-body">Estimated time</span>
            <span className="text-sm font-bold text-green-900 font-heading">{totalManualLabel}</span>
          </div>
        </div>
      )}

      {/* Generate button */}
      <Button
        className="w-full h-16 bg-green-900 hover:bg-green-800 text-white text-lg font-bold rounded-xl font-heading active:scale-95 transition-transform"
        data-testid="build-generate-btn"
        disabled={missionLoading || manualBlocks.length === 0}
        onClick={() => {
          const blocks = buildManualSessionBlocks();
          if (blocks.length === 0) { toast.error("Add at least one area"); return; }
          quickStart(blocks);
        }}>
        {missionLoading ? <LoadingText /> : (
          <span className="flex items-center gap-2"><Play className="w-5 h-5" /> Generate Session</span>
        )}
      </Button>
    </div>
  );


  const renderCourseFlow = () => {
    if (!courseMode) {
      return (
        <div className="animate-fade-in" data-testid="course-flow">
          <FlowHeader title="On Course" onBack={resetFlow} />
          <div className="space-y-3">
            <button onClick={() => setCourseMode("practice")}
              className="w-full bg-white border-2 border-gray-200 rounded-2xl p-5 flex items-center gap-4 hover:border-green-900 active:scale-[0.98] transition-all text-left"
              data-testid="course-mode-practice">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                <Target className="w-6 h-6 text-green-900" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-bold text-gray-900 font-heading">Practice Round</p>
                <p className="text-xs text-gray-600 font-body">Structured formats</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
            <button onClick={() => setCourseMode("competitive")}
              className="w-full bg-white border-2 border-gray-200 rounded-2xl p-5 flex items-center gap-4 hover:border-green-900 active:scale-[0.98] transition-all text-left"
              data-testid="course-mode-competitive">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                <Trophy className="w-6 h-6 text-green-900" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-bold text-gray-900 font-heading">Competitive Round</p>
                <p className="text-xs text-gray-600 font-body">Play and log your score</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      );
    }

    if (courseMode === "practice") {
      return (
        <div className="animate-fade-in" data-testid="practice-round-setup">
          <FlowHeader title="Practice Round" onBack={() => setCourseMode(null)} />
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-3 block font-body">Holes</label>
              <div className="grid grid-cols-4 gap-2">
                {[3, 6, 9, 18].map(h => (
                  <button key={h} onClick={() => setCourseSetup(p => ({ ...p, holes: h }))}
                    className={`h-14 rounded-xl border-2 text-center font-bold font-heading transition-all ${courseSetup.holes === h ? "bg-green-50 border-green-900 text-green-900" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}
                    data-testid={`practice-holes-${h}`}>{h}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-3 block font-body">Format</label>
              <div className="space-y-2">
                {ON_COURSE_FORMATS.map(fmt => (
                  <button key={fmt.value} onClick={() => setCourseSetup(p => ({ ...p, format: fmt.value }))}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${courseSetup.format === fmt.value ? "bg-green-50 border-green-900" : "bg-white border-gray-200 hover:border-gray-300"}`}
                    data-testid={`practice-format-${fmt.value}`}>
                    <span className={`text-sm font-bold font-heading ${courseSetup.format === fmt.value ? "text-green-900" : "text-gray-900"}`}>{fmt.label}</span>
                    <p className="text-xs text-gray-600 font-body mt-0.5">{fmt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <Button
              className="w-full h-16 bg-green-900 hover:bg-green-800 text-white text-lg font-bold rounded-xl font-heading"
              disabled={missionLoading}
              data-testid="start-practice-round-btn"
              onClick={() => {
                quickStart([{ location: "course", holes: courseSetup.holes, format: courseSetup.format, course_mode: "practice", primary_skill: "course" }]);
              }}>
              {missionLoading ? <LoadingText /> : <><Play className="w-5 h-5 mr-2" /> Start Practice Round</>}
            </Button>
          </div>
        </div>
      );
    }

    // Competitive
    return (
      <div className="animate-fade-in" data-testid="competitive-round-setup">
        <FlowHeader title="Competitive Round" onBack={() => setCourseMode(null)} />
        <div className="space-y-5">
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-3 block font-body">Holes</label>
            <div className="grid grid-cols-2 gap-3">
              {[9, 18].map(h => (
                <button key={h} onClick={() => setCourseSetup(p => ({ ...p, holes: h }))}
                  className={`h-20 rounded-xl border-2 flex flex-col items-center justify-center font-heading transition-all ${courseSetup.holes === h ? "bg-green-50 border-green-900 text-green-900" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}
                  data-testid={`comp-holes-${h}`}>
                  <span className="text-2xl font-extrabold">{h}</span>
                  <span className="text-xs text-gray-600 font-body">{h === 9 ? "~2 hrs" : "~4 hrs"}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-2 block font-body">Course <span className="normal-case font-normal text-gray-500">(optional)</span></label>
            <input type="text" value={courseSetup.course_name} maxLength={60}
              onChange={e => setCourseSetup(p => ({ ...p, course_name: e.target.value }))}
              placeholder="e.g., Royal Melbourne"
              className="w-full h-14 bg-white border-2 border-gray-200 rounded-xl px-4 text-gray-900 text-sm font-body placeholder:text-gray-400 focus:outline-none focus:border-green-900"
              data-testid="comp-course-name" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-2 block font-body">Focus <span className="normal-case font-normal text-gray-500">(optional)</span></label>
            <input type="text" value={courseSetup.focus} maxLength={80}
              onChange={e => setCourseSetup(p => ({ ...p, focus: e.target.value }))}
              placeholder="e.g., Commit to every shot"
              className="w-full h-14 bg-white border-2 border-gray-200 rounded-xl px-4 text-gray-900 text-sm font-body placeholder:text-gray-400 focus:outline-none focus:border-green-900"
              data-testid="comp-focus" />
          </div>
          <Button
            className="w-full h-16 bg-green-900 hover:bg-green-800 text-white text-lg font-bold rounded-xl font-heading"
            disabled={missionLoading}
            data-testid="start-competitive-round-btn"
            onClick={() => {
              quickStart([{
                location: "course", holes: courseSetup.holes, course_mode: "competitive", primary_skill: "course",
                ...(courseSetup.course_name ? { course_name: courseSetup.course_name } : {}),
                ...(courseSetup.focus ? { focus_area: courseSetup.focus } : {})
              }]);
            }}>
            {missionLoading ? <LoadingText /> : <><Play className="w-5 h-5 mr-2" /> Start Round</>}
          </Button>
        </div>
      </div>
    );
  };

  // ── MAIN RENDER ──

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="px-5 py-4 border-b-2 border-gray-200 bg-white">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-green-900 flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-extrabold text-lg text-gray-900 tracking-tight font-heading">GolfFlowPro</span>
            {user?.is_subscriber && (
              <span className="text-[10px] font-bold tracking-widest uppercase bg-green-50 text-green-900 border border-green-200 px-2 py-0.5 rounded-md">Pro</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {user?.role === "coach" && (
              <button onClick={() => navigate("/coach")} className="p-2 text-gray-600 hover:text-gray-900" data-testid="coach-dashboard-btn">
                <Users className="w-5 h-5" />
              </button>
            )}
            <button onClick={() => navigate("/profile")} className="p-2 text-gray-600 hover:text-gray-900" data-testid="settings-btn">
              <Settings className="w-5 h-5" />
            </button>
            <button onClick={handleLogout} className="p-2 text-gray-600 hover:text-gray-900" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="px-5 py-6">
        <div className="max-w-md mx-auto space-y-6">

          {/* Consent */}
          {user && !user.consent_accepted && (
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-5">
              <p className="text-gray-900 font-bold text-sm mb-1 font-heading">Privacy & Terms</p>
              <p className="text-gray-600 text-xs mb-3 font-body">
                By continuing, you agree to our <a href="/privacy" className="text-green-900 underline">Privacy Policy</a> and <a href="/terms" className="text-green-900 underline">Terms</a>.
              </p>
              <Button size="sm" className="bg-green-900 hover:bg-green-800 text-white text-xs h-9 px-4 rounded-lg" data-testid="accept-consent-btn"
                onClick={async () => {
                  try { const res = await authFetch(`${API}/api/consent/accept`, { method: "POST" }); if (res.ok) { setUser(prev => ({ ...prev, consent_accepted: true })); toast.success("Accepted"); } } catch (e) {}
                }}>I Agree</Button>
            </div>
          )}

          {/* Resume Session */}
          {resumeSession && !activeFlow && (
            <div className="bg-white border-2 border-green-200 rounded-2xl p-5 animate-fade-in" data-testid="resume-session-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <Play className="w-5 h-5 text-green-900" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-bold font-heading">Session in progress</p>
                  <p className="text-gray-600 text-xs font-body">Block {(resumeSession.currentBlockIndex || 0) + 1} waiting</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="text-gray-600 text-xs px-3 py-2 font-body" data-testid="dismiss-resume-btn"
                  onClick={() => { safeRemove("gfp_active_session"); setResumeSession(null); }}>Dismiss</button>
                <Button className="flex-1 h-14 bg-green-900 hover:bg-green-800 text-white font-bold rounded-xl font-heading" data-testid="resume-session-btn"
                  onClick={() => navigate(`/session/${resumeSession.missionId}`)}>
                  Resume Session
                </Button>
              </div>
            </div>
          )}

          {/* ── ACTIVE FLOW ── */}
          {activeFlow === "range" && renderRangeFlow()}
          {activeFlow === "putting" && renderPuttingFlow()}
          {activeFlow === "short_game" && renderShortGameFlow()}
          {activeFlow === "wedge_play" && renderWedgePlayFlow()}
          {activeFlow === "course" && renderCourseFlow()}
          {activeFlow === "build" && renderBuildFlow()}

          {/* ── HOME STATE ── */}
          {!activeFlow && (
            <>
              {/* Hero */}
              <div className="pt-2 animate-fade-in">
                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight font-heading" data-testid="hero-title">
                  Ready to practise?
                </h1>
                <p className="text-gray-500 text-sm font-body mt-1">Tell us what you have available</p>
              </div>

              {/* BUILD SESSION — Primary CTA */}
              <button
                onClick={() => { setActiveFlow("build"); setManualBlocks([]); }}
                className="w-full h-20 bg-green-900 hover:bg-green-800 text-white rounded-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-all animate-fade-in"
                data-testid="build-session-btn">
                <Plus className="w-6 h-6" />
                <span className="text-lg font-bold font-heading">Build Today's Session</span>
              </button>

              {/* Quick starts — single area shortcuts */}
              <div>
                <p className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-2 font-body">Or quick start</p>
                <div className="grid grid-cols-4 gap-2 stagger-children" data-testid="quick-starts">
                  {[
                    { id: "range", label: "Range", icon: Target },
                    { id: "putting", label: "Putting", icon: Flag },
                    { id: "short_game", label: "Short Game", icon: Zap },
                    { id: "course", label: "Course", icon: MapPin },
                  ].map(loc => (
                    <button key={loc.id}
                      onClick={() => setActiveFlow(loc.id)}
                      disabled={missionLoading}
                      className="h-16 bg-white border-2 border-gray-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-green-900 active:scale-95 transition-all"
                      data-testid={`action-${loc.id}`}>
                      <loc.icon className="w-4 h-4 text-green-900" />
                      <span className="text-[11px] font-bold text-gray-900 font-heading">{loc.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Wedge Play — separate row */}
              <button
                onClick={() => setActiveFlow("wedge_play")}
                disabled={missionLoading}
                className="w-full h-16 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center gap-3 hover:border-green-900 active:scale-[0.98] transition-all"
                data-testid="action-wedge-play">
                <Zap className="w-5 h-5 text-green-900" />
                <span className="text-sm font-bold text-gray-900 font-heading">Wedge Play</span>
                <span className="text-xs text-gray-500 font-body">40 – 100 yards</span>
              </button>

              {/* Weekly Progress */}
              {weeklyStatus?.has_plan && (
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-5" data-testid="weekly-progress">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold tracking-widest uppercase text-gray-500 font-body">This Week</span>
                    <span className="text-sm font-bold text-gray-900 font-heading">
                      {weeklyStatus.completed_count}/{weeklyStatus.target_sessions}
                    </span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-900 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (weeklyStatus.completed_count / weeklyStatus.target_sessions) * 100)}%` }} />
                  </div>
                </div>
              )}

              {/* Coach Section */}
              {user?.has_coach && (
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-5" data-testid="coach-section">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-900" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold tracking-widest uppercase text-gray-500 font-body">Connected Coach</p>
                      <p className="text-sm font-bold text-gray-900 font-heading">Drills active</p>
                    </div>
                    <button onClick={() => navigate("/coach-drills")} className="text-green-900 text-xs font-bold font-body" data-testid="view-coach-drills">View</button>
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3" data-testid="quick-stats">
                {[
                  { value: user?.sessions_completed || 0, label: "Sessions" },
                  { value: user?.total_practice_minutes || 0, label: "Minutes" },
                  { value: user?.current_streak || 0, label: "Streak" },
                ].map(s => (
                  <div key={s.label} className="bg-white border-2 border-gray-200 rounded-2xl p-4 text-center">
                    <p className="text-2xl font-extrabold text-green-900 font-heading">{s.value}</p>
                    <p className="text-xs text-gray-600 font-body">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Recent Sessions */}
              {recentSessions.length > 0 && (
                <div data-testid="recent-sessions">
                  <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-3 font-body">Recent</p>
                  <div className="space-y-2">
                    {recentSessions.slice(0, 3).map((session, index) => (
                      <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-900 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate font-heading">{session.title}</p>
                          <p className="text-xs text-gray-600 font-body">{session.date} · {session.duration_minutes}m</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {appVersion && (
                <p className="text-center text-xs text-gray-400 font-body" data-testid="app-version">Build {appVersion.version}</p>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// ── SHARED COMPONENTS ──

function FlowHeader({ title, onBack }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={onBack} className="p-2 -ml-2 text-gray-600 hover:text-gray-900 rounded-xl active:scale-95 transition-transform" data-testid="flow-back-btn">
        <ArrowLeft className="w-5 h-5" />
      </button>
      <h2 className="text-xl font-bold text-gray-900 font-heading">{title}</h2>
    </div>
  );
}

function LoadingIndicator() {
  return (
    <div className="flex items-center justify-center gap-2 text-green-900 text-sm py-4 font-body" data-testid="loading-indicator">
      <div className="w-5 h-5 border-2 border-green-900 border-t-transparent rounded-full animate-spin" />
      Building your session...
    </div>
  );
}

function LoadingText() {
  return (
    <span className="flex items-center gap-2">
      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
      Building...
    </span>
  );
}
